
import { format } from 'date-fns';
import { FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { exportRequestToPDF } from "@/utils/pdfExport";
import { type ItineraryRequest } from "@/hooks/useItineraryRequests";
import { toast } from 'sonner';

type ItineraryRequestDetailProps = {
  request: ItineraryRequest | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ItineraryRequestDetail({
  request,
  isOpen,
  onOpenChange,
}: ItineraryRequestDetailProps) {
  if (!request) return null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-semibold">New</span>;
      case "in-progress":
        return <span className="bg-secondary text-secondary-foreground px-2 py-1 rounded-full text-xs font-semibold">In Progress</span>;
      case "converted":
        return <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">Converted</span>;
      case "declined":
        return <span className="bg-destructive text-destructive-foreground px-2 py-1 rounded-full text-xs font-semibold">Declined</span>;
      default:
        return <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  const handleExportPDF = () => {
    try {
      exportRequestToPDF(request);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Itinerary Request Details</DialogTitle>
          <DialogDescription>
            Request from {request.name} on {format(new Date(request.created_at), 'MMMM d, yyyy')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="font-medium">Status:</div>
            <div className="col-span-3">{getStatusBadge(request.status)}</div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="font-medium">Email:</div>
            <div className="col-span-3">{request.email}</div>
          </div>
          
          {request.phone && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="font-medium">Phone:</div>
              <div className="col-span-3">{request.phone}</div>
            </div>
          )}
          
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="font-medium">Destination:</div>
            <div className="col-span-3">{request.destination}</div>
          </div>
          
          {request.trip_type && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="font-medium">Trip Type:</div>
              <div className="col-span-3">{request.trip_type}</div>
            </div>
          )}
          
          {request.travel_dates && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="font-medium">Travel Dates:</div>
              <div className="col-span-3">{request.travel_dates}</div>
            </div>
          )}
          
          {request.budget && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="font-medium">Budget:</div>
              <div className="col-span-3">{request.budget}</div>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-4">
            <div className="font-medium">Message:</div>
            <div className="whitespace-pre-wrap border rounded-md p-3 bg-muted/30">
              {request.message}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleExportPDF}
          >
            <FileText className="mr-2 h-4 w-4" />
            Export as PDF
          </Button>
          <Button 
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
